import { Emitter } from '../core/emitter';
import type { MidiEvent } from '../midi/types';

export const TRANSPORT_STATES = {
  PLAYING: 'PLAYING',
  WAITING_FOR_INPUT: 'WAITING_FOR_INPUT',
  RECORDING: 'RECORDING',
  STOPPED: 'STOPPED',
} as const;

export type TransportState = (typeof TRANSPORT_STATES)[keyof typeof TRANSPORT_STATES];

export interface ScheduleEntry {
  timestamp: number;
  data: MidiEvent;
}

type SchedulerEvents = {
  tick: { ratio: number; entries: ScheduleEntry[] };
  added: ScheduleEntry;
  record: TransportState;
  endRecord: number;
  end: void;
};

const DEFAULT_TICK_LENGTH = 20;
const DEFAULT_LENGTH = 1000;

/**
 * A loop recorder/player for MIDI events.
 *
 * Timing is measured against `performance.now()` rather than `Date.now()` so a
 * system clock adjustment cannot jump the playhead, and the tick loop is
 * cancellable instead of relying on a `playing` flag read at wake-up.
 */
export class Scheduler extends Emitter<SchedulerEvents> {
  static readonly STATES = TRANSPORT_STATES;

  tickLength = DEFAULT_TICK_LENGTH;
  length = DEFAULT_LENGTH;
  loop = true;
  waitForInput = false;
  playing = false;
  recording = false;

  private startTime = performance.now();
  private started = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private schedule: ScheduleEntry[] = [];
  private pending: ScheduleEntry[] = [];
  private _state: TransportState = TRANSPORT_STATES.STOPPED;
  private _speed = 1;

  get state(): TransportState {
    return this._state;
  }

  set state(value: TransportState) {
    this._state = value;
  }

  get speed(): number {
    return this._speed;
  }

  set speed(value: number) {
    this._speed = value;
  }

  /** Position within the loop, 0-1. */
  get timeRatio(): number {
    return ((performance.now() - this.startTime) * this._speed) / this.length;
  }

  /** Position within the loop in milliseconds. */
  get time(): number {
    return this.timeRatio * this.length;
  }

  serialize(): string {
    return JSON.stringify(this.schedule);
  }

  addToSchedule(data: MidiEvent, timestamp = this.time): void {
    // In "wait for input" recording the clock does not start until the first
    // event arrives, so that entry lands at position zero.
    let at = timestamp;
    if (this.waitForInput && !this.started && this.recording) {
      this._state = TRANSPORT_STATES.RECORDING;
      this.started = true;
      this.startTime = performance.now();
      at = 0;
      this.emit('record', this._state);
    }
    const entry: ScheduleEntry = { timestamp: at, data };
    this.schedule.push(entry);
    this.emit('added', entry);
  }

  reset(): ScheduleEntry[] {
    const previous = this.schedule;
    this.schedule = [];
    this.pending = [];
    this.length = DEFAULT_LENGTH;
    return previous;
  }

  record(waitForInput = true): void {
    this._state = waitForInput ? TRANSPORT_STATES.WAITING_FOR_INPUT : TRANSPORT_STATES.RECORDING;
    if (!waitForInput) this.emit('record', this._state);
    this.waitForInput = waitForInput;
    this.started = !waitForInput;
    this.startTime = performance.now();
    this.recording = true;
  }

  play(): void {
    this.stopTimer();
    this._state = TRANSPORT_STATES.PLAYING;
    this.playing = true;
    this.pending = this.schedule.slice();
    this.startTime = performance.now();
    this.tick();
  }

  stop(): void {
    this.stopTimer();
    if (this.recording) {
      this.started = false;
      this.length = Math.max(this.time, this.tickLength);
      this.recording = false;
      this.emit('endRecord', this.length);
    } else {
      this.emit('end', undefined);
    }
    this._state = TRANSPORT_STATES.STOPPED;
    this.playing = false;
  }

  seek(ratio: number): void {
    this.startTime = performance.now() - this.length * ratio;
  }

  dispose(): void {
    this.stopTimer();
    this.clear();
  }

  private stopTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private tick = (): void => {
    this.timer = null;

    if (this.timeRatio > 1) {
      if (this.loop) this.play();
      else this.stop();
      return;
    }

    if (this.playing) this.timer = setTimeout(this.tick, this.tickLength);

    const now = this.time;
    const due = now < this.length ? this.pending.filter((e) => e.timestamp < now + this.tickLength) : [];

    this.emit('tick', { ratio: this.timeRatio, entries: due });

    if (due.length > 0) this.pending = this.pending.filter((entry) => !due.includes(entry));
  };
}
